import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Button, Alert, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, ToggleButton, ToggleButtonGroup,
  Divider, List, ListItemButton, ListItemText, Chip, MenuItem, FormControlLabel, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
} from '@mui/material';
import { PointOfSale, Add, Remove, Delete, QrCodeScanner, CheckCircle, Tune, Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { parseScaleBarcode } from '../utils/scaleBarcode';
import type { PosProduct, PosVariant, PosSaleResult } from '../types';
import TextField from '../components/common/TextField';

interface CartItem {
  key: string;
  productId: number;
  productVariantId?: number;
  variantLabel?: string;
  variantStock?: number;
  name: string;
  sku?: string | null;
  price: number;
  qty: number;
}

interface PosCustomer { id: number; name: string; }
type ExtraKind = 'none' | 'descuento' | 'recargo';
type ExtraMode = 'pct' | 'fixed';

const fmt = (v: number) => `$${(v ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PAYMENT_METHODS = ['Efectivo', 'Tarjeta', 'Transferencia', 'QR'];
const DOC_TIPOS = [
  { value: 99, label: 'Consumidor Final' },
  { value: 80, label: 'CUIT' },
  { value: 96, label: 'DNI' },
];

export const PosPage: React.FC = () => {
  const navigate = useNavigate();
  const [hasCash, setHasCash] = useState<boolean | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<PosProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('Efectivo');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cobro: pago dividido entre varios medios + vuelto sobre la parte en efectivo.
  const [splitMode, setSplitMode] = useState(false);
  const [splitAmounts, setSplitAmounts] = useState<Record<string, string>>({});
  const [cashReceived, setCashReceived] = useState('');

  // Extras: descuento / recargo (porcentaje o monto fijo)
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [extraKind, setExtraKind] = useState<ExtraKind>('none');
  const [extraMode, setExtraMode] = useState<ExtraMode>('pct');
  const [extraValue, setExtraValue] = useState('0');

  // Cliente
  const [customer, setCustomer] = useState<PosCustomer | null>(null);
  const [customerOptions, setCustomerOptions] = useState<PosCustomer[]>([]);

  // Selección de variante (cuando un producto tiene talles/colores)
  const [variantPicker, setVariantPicker] = useState<PosProduct | null>(null);

  // Facturación opcional
  const [emitInvoice, setEmitInvoice] = useState(false);
  const [cbteTipo, setCbteTipo] = useState(6);
  const [docTipo, setDocTipo] = useState(99);
  const [docNro, setDocNro] = useState('');
  const [emailTo, setEmailTo] = useState('');

  const [result, setResult] = useState<PosSaleResult | null>(null);

  useEffect(() => {
    (async () => {
      try { setHasCash(!!(await api.getCurrentCashSession())); }
      catch { setHasCash(false); }
    })();
  }, []);

  const focusInput = () => setTimeout(() => inputRef.current?.focus(), 50);

  const addToCart = (p: PosProduct, variant?: PosVariant) => {
    const key = `${p.id}:${variant?.id ?? 0}`;
    setCart(prev => {
      const ex = prev.find(i => i.key === key);
      if (ex) return prev.map(i => i.key === key ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, {
        key, productId: p.id, productVariantId: variant?.id,
        variantLabel: variant?.label, variantStock: variant?.stock,
        name: p.name, sku: p.sku, price: p.price, qty: 1,
      }];
    });
    setSearch(''); setResults([]); focusInput();
  };

  // Agrega una línea con cantidad y precio explícitos (balanza por peso o por precio).
  const addWeighted = (p: PosProduct, qty: number, unitPrice: number) => {
    const key = `${p.id}:weight:${Date.now()}`;
    setCart(prev => [...prev, {
      key, productId: p.id, name: p.name, sku: p.sku,
      price: Math.round(unitPrice * 100) / 100, qty: Math.round(qty * 1000) / 1000,
    }]);
    setSearch(''); setResults([]); focusInput();
  };

  // Si el producto tiene variantes, hay que elegir una antes de agregar.
  const pickOrAdd = (p: PosProduct) => {
    if (p.variants && p.variants.length > 0) { setVariantPicker(p); setResults([]); }
    else addToCart(p);
  };

  const onScan = async () => {
    const code = search.trim();
    if (!code) return;
    try {
      // 1) ¿Es un código de balanza (peso/precio embebido)?
      const scale = parseScaleBarcode(code);
      if (scale) {
        const byPlu = await api.posLookup(scale.plu);
        const prod = byPlu.find(p => p.sku === scale.plu || String(p.id) === scale.plu) || byPlu[0];
        if (prod) {
          if (prod.soldByWeight) addWeighted(prod, scale.weightKg, prod.price); // precio por kg × peso
          else addWeighted(prod, 1, scale.embeddedPrice); // precio total embebido
          return;
        }
      }
      // 2) Búsqueda normal por barcode/SKU/nombre.
      const found = await api.posLookup(code);
      if (found.length === 1) pickOrAdd(found[0]);
      else setResults(found);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al buscar el producto');
    }
  };

  const setQty = (key: string, qty: number) =>
    setCart(prev => prev.map(i => i.key === key ? { ...i, qty: Math.max(1, qty) } : i));
  const removeItem = (key: string) => setCart(prev => prev.filter(i => i.key !== key));

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);

  // Monto del extra calculado sobre el subtotal
  const extraAmount = useMemo(() => {
    if (extraKind === 'none') return 0;
    const v = Number(extraValue) || 0;
    return extraMode === 'pct' ? Math.round(subtotal * (v / 100) * 100) / 100 : v;
  }, [extraKind, extraMode, extraValue, subtotal]);
  const discountAmount = extraKind === 'descuento' ? extraAmount : 0;
  const surchargeAmount = extraKind === 'recargo' ? extraAmount : 0;
  const total = Math.max(0, subtotal - discountAmount + surchargeAmount);

  // Medios de pago efectivos de la venta: el desglose si está dividido, o el método único.
  const payments = useMemo(() => {
    if (!splitMode) return [{ method: paymentMethod, amount: total }];
    return PAYMENT_METHODS
      .map(m => ({ method: m, amount: Math.round((Number(splitAmounts[m]) || 0) * 100) / 100 }))
      .filter(p => p.amount > 0);
  }, [splitMode, splitAmounts, paymentMethod, total]);

  const assigned = useMemo(() => payments.reduce((s, p) => s + p.amount, 0), [payments]);
  const pending = Math.round((total - assigned) * 100) / 100;
  const cashPortion = useMemo(
    () => payments.filter(p => p.method === 'Efectivo').reduce((s, p) => s + p.amount, 0),
    [payments],
  );
  const change = useMemo(() => {
    const received = Number(cashReceived) || 0;
    return received > cashPortion ? Math.round((received - cashPortion) * 100) / 100 : 0;
  }, [cashReceived, cashPortion]);

  // En pago dividido no se puede cobrar hasta que el desglose cierre contra el total.
  const paymentsBalanced = !splitMode || (payments.length > 0 && Math.abs(pending) < 0.01);

  const setSplitAmount = (method: string, value: string) =>
    setSplitAmounts(prev => ({ ...prev, [method]: value }));

  /** Completa el medio indicado con lo que falta para llegar al total. */
  const assignRest = (method: string) => {
    const others = PAYMENT_METHODS
      .filter(m => m !== method)
      .reduce((s, m) => s + (Number(splitAmounts[m]) || 0), 0);
    const rest = Math.round((total - others) * 100) / 100;
    setSplitAmount(method, rest > 0 ? String(rest) : '');
  };

  const searchCustomers = async (q: string) => {
    try {
      const res = await api.getCustomers({ page: 1, pageSize: 10, search: q });
      setCustomerOptions((res.customers || []).map((c: any) => ({ id: c.id, name: c.legalName || c.displayName || c.name || `Cliente #${c.id}` })));
    } catch { setCustomerOptions([]); }
  };

  const cobrar = async () => {
    if (cart.length === 0) return;
    setBusy(true); setError(null);
    try {
      const res = await api.posSale({
        items: cart.map(i => ({ productId: i.productId, productVariantId: i.productVariantId ?? null, quantity: i.qty, unitPrice: i.price })),
        paymentMethod,
        payments: splitMode ? payments : undefined,
        cashReceived: cashPortion > 0 && Number(cashReceived) > 0 ? Number(cashReceived) : undefined,
        customerId: customer?.id ?? null,
        discount: discountAmount,
        surcharge: surchargeAmount,
        emitInvoice,
        invoice: emitInvoice ? { cbteTipo, concepto: 1, docTipo, docNro: docTipo === 99 ? 0 : Number(docNro.replace(/\D/g, '')) || 0, condicionIvaReceptorId: docTipo === 99 ? 5 : 1 } : undefined,
        emailTo: emitInvoice && emailTo ? emailTo : undefined,
      });
      setResult(res);
    } catch (e: any) {
      const data = e?.response?.data;
      setError(data?.message || 'Error al registrar la venta');
      if (e?.response?.status === 409) setHasCash(false);
    } finally {
      setBusy(false);
    }
  };

  const newSale = () => {
    setResult(null); setCart([]); setEmitInvoice(false); setDocNro(''); setEmailTo('');
    setExtraKind('none'); setExtraMode('pct'); setExtraValue('0'); setCustomer(null);
    setPaymentMethod('Efectivo'); setSplitMode(false); setSplitAmounts({}); setCashReceived('');
    focusInput();
  };

  const printTicket = () => {
    if (!result) return;
    const rows = cart.map(i => `<tr><td>${i.qty}x ${i.name}</td><td style="text-align:right">${fmt(i.price * i.qty)}</td></tr>`).join('');
    const inv = result.invoice?.cae
      ? `<p>Comprobante ${String(result.invoice.ptoVta).padStart(5, '0')}-${String(result.invoice.cbteNro).padStart(8, '0')}<br/>CAE: ${result.invoice.cae}</p>`
      : '';
    const pays = (result.payments?.length ?? 0) > 1
      ? `<table>${result.payments!.map(p => `<tr><td>${p.method}</td><td style="text-align:right">${fmt(p.amount)}</td></tr>`).join('')}</table>`
      : '';
    const vuelto = (result.change ?? 0) > 0
      ? `<p style="text-align:right"><b>VUELTO ${fmt(result.change!)}</b></p>`
      : '';
    const w = window.open('', '_blank', 'width=320,height=600');
    if (!w) return;
    w.document.write(`<html><head><title>Ticket ${result.reference}</title>
      <style>body{font-family:monospace;width:280px;padding:8px} table{width:100%;border-collapse:collapse} td{padding:2px 0;font-size:12px} h3{text-align:center}</style>
      </head><body><h3>Ticket</h3><p>${result.reference}<br/>${new Date().toLocaleString('es-AR')}</p>
      <table>${rows}</table><hr/><p style="text-align:right"><b>TOTAL ${fmt(result.total)}</b></p>
      <p>Pago: ${result.paymentMethod}</p>${pays}${vuelto}${inv}<p style="text-align:center">¡Gracias por su compra!</p>
      <script>window.print();</script></body></html>`);
    w.document.close();
  };

  if (hasCash === false) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <PointOfSale color="primary" /><Typography variant="h4" fontWeight={700}>Punto de Venta</Typography>
        </Stack>
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => navigate('/caja')}>Abrir caja</Button>}>
          No hay una caja abierta. Abrí la caja para poder vender.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <PointOfSale color="primary" /><Typography variant="h4" fontWeight={700}>Punto de Venta</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* Columna izquierda: escaneo + carrito */}
        <Card sx={{ flex: 2, width: '100%' }}>
          <CardContent>
            <TextField
              fullWidth autoFocus inputRef={inputRef}
              placeholder="Escaneá un código de barras o buscá por nombre/SKU…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onScan(); } }}
              InputProps={{ startAdornment: <QrCodeScanner sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />

            {results.length > 0 && (
              <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 1, maxHeight: 220, overflow: 'auto' }}>
                {results.map(p => (
                  <ListItemButton key={p.id} onClick={() => pickOrAdd(p)}>
                    <ListItemText
                      primary={p.name}
                      secondary={`${p.sku || ''} · stock ${p.currentStock}${p.variants && p.variants.length ? ` · ${p.variants.length} variantes` : ''}`}
                    />
                    <Typography fontWeight={600}>{fmt(p.price)}</Typography>
                  </ListItemButton>
                ))}
              </List>
            )}

            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell align="center" sx={{ width: 140 }}>Cant.</TableCell>
                  <TableCell align="right">Precio</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                  <TableCell sx={{ width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {cart.map(i => (
                  <TableRow key={i.key}>
                    <TableCell>
                      {i.name}{i.sku ? <Typography variant="caption" color="text.secondary"> · {i.sku}</Typography> : null}
                      {i.variantLabel ? <Chip size="small" label={i.variantLabel} sx={{ ml: 1 }} /> : null}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <IconButton size="small" onClick={() => setQty(i.key, i.qty - 1)}><Remove fontSize="small" /></IconButton>
                        <Typography sx={{ minWidth: 24, textAlign: 'center' }}>{i.qty}</Typography>
                        <IconButton size="small" onClick={() => setQty(i.key, i.qty + 1)}><Add fontSize="small" /></IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{fmt(i.price)}</TableCell>
                    <TableCell align="right">{fmt(i.price * i.qty)}</TableCell>
                    <TableCell><IconButton size="small" color="error" onClick={() => removeItem(i.key)}><Delete fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
                {cart.length === 0 && (
                  <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">Carrito vacío. Escaneá un producto para empezar.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Columna derecha: cobro */}
        <Card sx={{ flex: 1, width: '100%', position: { md: 'sticky' }, top: 16 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Cobro</Typography>
            <Stack spacing={2}>
              <Autocomplete
                size="small"
                options={customerOptions}
                value={customer}
                getOptionLabel={o => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(_, v) => setCustomer(v)}
                onInputChange={(_, v) => v && searchCustomers(v)}
                renderInput={params => (
                  <TextField {...params} label="Cliente"
                    placeholder="Consumidor Final"
                    InputProps={{ ...params.InputProps, startAdornment: <Person sx={{ mr: 0.5, color: 'text.secondary' }} fontSize="small" /> }} />
                )}
              />
              <Stack direction="row" justifyContent="space-between"><span>Subtotal</span><b>{fmt(subtotal)}</b></Stack>
              {discountAmount > 0 && <Stack direction="row" justifyContent="space-between" color="success.main"><span>Descuento</span><b>- {fmt(discountAmount)}</b></Stack>}
              {surchargeAmount > 0 && <Stack direction="row" justifyContent="space-between" color="warning.main"><span>Recargo</span><b>+ {fmt(surchargeAmount)}</b></Stack>}
              <Button variant="outlined" size="small" startIcon={<Tune />} onClick={() => setExtrasOpen(true)}>Gestionar Extras</Button>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography>Total</Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main">{fmt(total)}</Typography>
              </Stack>

              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">Método de pago</Typography>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={<Switch size="small" checked={splitMode} onChange={(_, v) => { setSplitMode(v); setSplitAmounts({}); }} />}
                  label={<Typography variant="body2">Dividir</Typography>}
                />
              </Stack>

              {!splitMode ? (
                <ToggleButtonGroup exclusive size="small" value={paymentMethod} onChange={(_, v) => v && setPaymentMethod(v)} fullWidth>
                  {PAYMENT_METHODS.map(m => <ToggleButton key={m} value={m}>{m}</ToggleButton>)}
                </ToggleButtonGroup>
              ) : (
                <Stack spacing={1}>
                  {PAYMENT_METHODS.map(m => (
                    <Stack key={m} direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small" label={m} type="number" fullWidth
                        value={splitAmounts[m] ?? ''}
                        onChange={e => setSplitAmount(m, e.target.value)}
                        InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography> }}
                      />
                      <Button size="small" onClick={() => assignRest(m)} disabled={total <= 0}>Resto</Button>
                    </Stack>
                  ))}
                  {Math.abs(pending) >= 0.01 && (
                    <Alert severity={pending > 0 ? 'info' : 'warning'} sx={{ py: 0 }}>
                      {pending > 0 ? `Falta asignar ${fmt(pending)}` : `Te pasaste por ${fmt(Math.abs(pending))}`}
                    </Alert>
                  )}
                </Stack>
              )}

              {cashPortion > 0 && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small" label="Paga con" type="number" fullWidth
                    value={cashReceived}
                    onChange={e => setCashReceived(e.target.value)}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography> }}
                  />
                  <Button size="small" onClick={() => setCashReceived(String(cashPortion))}>Justo</Button>
                </Stack>
              )}
              {change > 0 && (
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="subtitle2">Vuelto</Typography>
                  <Typography variant="h5" fontWeight={700} color="warning.main">{fmt(change)}</Typography>
                </Stack>
              )}

              <FormControlLabel control={<Switch checked={emitInvoice} onChange={(_, v) => setEmitInvoice(v)} />} label="Facturar (CAE)" />
              {emitInvoice && (
                <Stack spacing={1.5}>
                  <TextField select size="small" label="Comprobante" value={cbteTipo} onChange={e => setCbteTipo(Number(e.target.value))}>
                    <MenuItem value={6}>Factura B</MenuItem>
                    <MenuItem value={1}>Factura A</MenuItem>
                    <MenuItem value={11}>Factura C</MenuItem>
                  </TextField>
                  <Stack direction="row" spacing={1}>
                    <TextField select size="small" label="Documento" value={docTipo} fullWidth
                      onChange={e => { const v = Number(e.target.value); setDocTipo(v); if (v === 99) setDocNro(''); }}>
                      {DOC_TIPOS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="N° doc" value={docTipo === 99 ? '' : docNro}
                      disabled={docTipo === 99} onChange={e => setDocNro(e.target.value)} />
                  </Stack>
                  <TextField size="small" label="Email del cliente (opcional)" type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} />
                </Stack>
              )}

              <Button variant="contained" size="large" disabled={busy || cart.length === 0 || !paymentsBalanced} onClick={cobrar}>
                {busy ? 'Procesando…' : `Cobrar ${fmt(total)}`}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Resultado de la venta */}
      <Dialog open={!!result} onClose={newSale} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center"><CheckCircle color="success" /><span>Venta registrada</span></Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography>Total cobrado: <b>{fmt(result?.total || 0)}</b> ({result?.paymentMethod})</Typography>
            {(result?.payments?.length ?? 0) > 1 && (
              <Stack spacing={0.25}>
                {result!.payments!.map(p => (
                  <Stack key={p.method} direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">{p.method}</Typography>
                    <Typography variant="body2">{fmt(p.amount)}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
            {(result?.change ?? 0) > 0 && (
              <Alert severity="warning" icon={false}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
                  <span>Vuelto</span>
                  <Typography variant="h6" fontWeight={700}>{fmt(result!.change!)}</Typography>
                </Stack>
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">Ref: {result?.reference}</Typography>
            {result?.invoice?.cae && (
              <Alert severity="success">Comprobante {String(result.invoice.ptoVta).padStart(5, '0')}-{String(result.invoice.cbteNro).padStart(8, '0')} · CAE {result.invoice.cae}</Alert>
            )}
            {result?.emailSent && <Chip color="success" size="small" label="Factura enviada por email" />}
            {result?.warning && <Alert severity="warning">{result.warning}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={printTicket}>Imprimir ticket</Button>
          <Button variant="contained" onClick={newSale}>Nueva venta</Button>
        </DialogActions>
      </Dialog>

      {/* Gestionar Extras */}
      <Dialog open={extrasOpen} onClose={() => setExtrasOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Gestionar Extras</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">Aplica descuentos o recargos a la venta actual.</Typography>
            <Stack direction="row" justifyContent="space-between"><span>Subtotal:</span><b>{fmt(subtotal)}</b></Stack>
            <ToggleButtonGroup exclusive size="small" fullWidth value={extraKind} onChange={(_, v) => v && setExtraKind(v)}>
              <ToggleButton value="none">Ninguno</ToggleButton>
              <ToggleButton value="descuento">Descuento</ToggleButton>
              <ToggleButton value="recargo">Recargo</ToggleButton>
            </ToggleButtonGroup>
            {extraKind !== 'none' && (
              <>
                <ToggleButtonGroup exclusive size="small" fullWidth value={extraMode} onChange={(_, v) => v && setExtraMode(v)}>
                  <ToggleButton value="pct">Porcentaje</ToggleButton>
                  <ToggleButton value="fixed">Monto fijo</ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  type="number" size="small" autoFocus
                  label={extraMode === 'pct' ? 'Porcentaje (%)' : 'Monto ($)'}
                  value={extraValue} onChange={e => setExtraValue(e.target.value)}
                />
                <Stack direction="row" justifyContent="space-between">
                  <span>{extraKind === 'descuento' ? 'Descuento' : 'Recargo'}:</span>
                  <b>{extraKind === 'descuento' ? '- ' : '+ '}{fmt(extraAmount)}</b>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <span>Total:</span><Typography variant="h6" color="primary.main">{fmt(total)}</Typography>
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setExtraKind('none'); setExtraValue('0'); }}>Quitar</Button>
          <Button variant="contained" onClick={() => setExtrasOpen(false)}>Aplicar</Button>
        </DialogActions>
      </Dialog>

      {/* Selección de variante */}
      <Dialog open={!!variantPicker} onClose={() => setVariantPicker(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Elegí una variante — {variantPicker?.name}</DialogTitle>
        <DialogContent>
          <List dense>
            {(variantPicker?.variants || []).map(v => (
              <ListItemButton key={v.id} disabled={v.stock <= 0}
                onClick={() => { if (variantPicker) { addToCart(variantPicker, v); setVariantPicker(null); } }}>
                <ListItemText primary={v.label} secondary={`Stock: ${v.stock}`} />
                <Typography fontWeight={600}>{fmt(variantPicker?.price || 0)}</Typography>
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVariantPicker(null)}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PosPage;
